import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';

// Assume API_BASE_URL is defined somewhere accessible, e.g., an .env file
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SpeakingForm = () => {
    // State variables for loading, submission status, and errors, consistent with ConsultingForm
    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [submitStatusType, setSubmitStatusType] = useState(null);
    const [errors, setErrors] = useState({}); // Placeholder for potential validation errors

    // Styles copied directly from ConsultingForm
    const boxStyle = {
        width: '100%',
        // maxWidth: '400px',
        padding: '20px',
        border: '1px solid violet',
        backgroundColor: 'rgba(238, 130, 238, 0.1)',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flexGrow: 1,
    };

    // Styles for input/textarea/select fields, copied directly from ConsultingForm
    const inputFieldStyle = {
        backgroundColor: 'transparent',
        fontSize: '1.6rem', // Base font size
        border: '1px solid violet',
    };

    // Placeholder for handleSubmit function, to match the ConsultingForm's structure
    // This function doesn't actually send data to an API in this component,
    // but demonstrates how it would be structured.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitStatus(null);
        setSubmitStatusType(null);
        setErrors({});

        // In a real application, you would collect form data here
        // const formData = new FormData(e.target);
        // const data = Object.fromEntries(formData.entries());

        // Simulate an API call
        try {
            // Replace with actual API call if needed
            // const response = await fetch(`${API_BASE_URL}/speaker-requests`, {
            //     method: 'POST',
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify(data),
            // });
            // const result = await response.json();

            // Simulate success or failure
            const success = Math.random() > 0.3; // 70% chance of success
            if (success) {
                setSubmitStatus('Speaker request submitted successfully!');
                setSubmitStatusType('success');
                e.target.reset(); // Clear form on successful submission
            } else {
                // Simulate an error
                setErrors({
                    firstName: ['First Name is required.'],
                    email: ['Please enter a valid email address.'],
                });
                setSubmitStatus('Please fix the errors below and try again.');
                setSubmitStatusType('danger');
            }
        } catch (error) {
            console.error('Submission error:', error);
            setSubmitStatus('Network or server error. Please try again.');
            setSubmitStatusType('danger');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to check for errors, consistent with ConsultingForm
    const hasError = (field) => errors && errors[field] && errors[field].length > 0;

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                <h3 className="mb-3 text-center">Event & Contact Details</h3>
                    {/* Applied the boxStyle to the Form component */}
                    <Form className='px-5' style={boxStyle} onSubmit={handleSubmit} noValidate>

                        {/* --- Section: Basic Event & Contact Information --- */}
                        

                        <Form.Group className="mb-3" controlId="event_name">
                            <Form.Label>Event Name *</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="event_name" // Added name attribute for form data collection
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('event_name')} // Added error invalidation
                            />
                            {/* Added feedback for errors */}
                            <Form.Control.Feedback type="invalid">{hasError('event_name') && errors.event_name[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="first_name">
                            <Form.Label>First Name *</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="first_name" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('first_name')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('first_name') && errors.first_name[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="last_name">
                            <Form.Label>Last Name *</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="last_name" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('last_name')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('last_name') && errors.last_name[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="email">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control 
                                type="email" 
                                name="email" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('email')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('email') && errors.email[0]}</Form.Control.Feedback>
                        </Form.Group>

                        {/* --- Phone Number Field (as requested) --- */}
                        <Form.Group className="mb-3" controlId="phone_number">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control 
                                type="tel" 
                                name="phone_number" // Added name attribute
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('phone_number')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('phone_number') && errors.phone_number[0]}</Form.Control.Feedback>
                        </Form.Group>

                        {/* --- WhatsApp Number Field (as requested) --- */}
                        <Form.Group className="mb-3" controlId="whatsapp_number">
                            <Form.Label>WhatsApp Number</Form.Label>
                            <Form.Control 
                                type="tel" 
                                name="whatsapp_number" // Added name attribute
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('whatsapp_number')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('whatsapp_number') && errors.whatsapp_number[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="business_description">
                            <Form.Label>
                                Business Description 1-3 Sentences (who you help, what, how, how much) *
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                name="business_description" // Added name attribute
                                required
                                style={{ ...inputFieldStyle, height: '150px' }} // Applied input field style and height
                                isInvalid={hasError('business_description')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('business_description') && errors.business_description[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="speaker_request">
                            <Form.Label>Who are you requesting to Speak? *</Form.Label>
                            <div>
                                <Form.Check
                                    type="checkbox"
                                    label="Jahid Hasan"
                                    id="speak-jahid"
                                    name="speaker_request" // Added name attribute for form data collection
                                    value="Jahid Hasan"
                                    isInvalid={hasError('speaker_request')} // Added error invalidation
                                />
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('speaker_request') && errors.speaker_request[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="speaking_date">
                            <Form.Label>Date of Speaking engagement*</Form.Label>
                            <Form.Control 
                                type="date" 
                                name="speaking_date" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('speaking_date')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('speaking_date') && errors.speaking_date[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="event_time">
                            <Form.Label>What time is the event and what time is Jahid expected to arrive/speak?*</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                name="event_time" // Added name attribute
                                required 
                                style={{ ...inputFieldStyle, height: '100px' }} // Applied input field style and height
                                isInvalid={hasError('event_time')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('event_time') && errors.event_time[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="speaking_location">
                            <Form.Label>Location of Speaking Engagement*</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="speaking_location" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('speaking_location')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('speaking_location') && errors.speaking_location[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="speaking_length">
                            <Form.Label>What is the length of time you'll need Jahid Hasan on stage or for an interview?*</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="speaking_length" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('speaking_length')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('speaking_length') && errors.speaking_length[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="talking_points">
                            <Form.Label>Please provide talking points you'd like Jahid Hasan to touch on.*</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="talking_points" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('talking_points')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('talking_points') && errors.talking_points[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="keynote_speaker">
                            <Form.Label>Will Jahid Hasan be the keynote speaker on the main stage?*</Form.Label>
                            <div>
                                {["YES", "NO"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="keynote_speaker" // Updated name
                                        label={option}
                                        id={`keynote-${index}`}
                                        value={option}
                                        isInvalid={hasError('keynote_speaker')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('keynote_speaker') && errors.keynote_speaker[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="audience_size">
                            <Form.Label>Estimated audience size for the event? At what date will the audience size be confirmed?*</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="audience_size" // Added name attribute
                                required 
                                style={inputFieldStyle} // Applied input field style
                                isInvalid={hasError('audience_size')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('audience_size') && errors.audience_size[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="advertise_event">
                            <Form.Label>Would you like to use Jahid Hasan to advertise the event?*</Form.Label>
                            <div>
                                {["YES", "NO"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="advertise_event" // Updated name
                                        label={option}
                                        id={`advertise-${index}`}
                                        value={option}
                                        isInvalid={hasError('advertise_event')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('advertise_event') && errors.advertise_event[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="social_media_links">
                            <Form.Label>Please list your social media links below (Instagram, LinkedIn, YouTube, etc.)*</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                name="social_media_links" // Added name attribute
                                required 
                                style={{ ...inputFieldStyle, height: '150px' }} // Applied input field style and height
                                isInvalid={hasError('social_media_links')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('social_media_links') && errors.social_media_links[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="event_content_distribution">
                            <Form.Label>
                                How will the event content be distributed? (Check all that apply)*
                            </Form.Label>
                            <Form.Check type="checkbox" label="Social Media" name="event_content_distribution" value="social-media" id="sm"/>
                            <Form.Check type="checkbox" label="Paid Members Area" name="event_content_distribution" value="paid-members-area" id="pm"/>
                            <Form.Check type="checkbox" label="Recording for Sale" name="event_content_distribution" value="recording-for-sale" id="rs"/>
                            <Form.Check type="checkbox" label="Free Group" name="event_content_distribution" value="free-group" id="fg"/>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('event_content_distribution') && errors.event_content_distribution[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="hotel_flights_agreement">
                            <Form.Label>We require hotel and flights to be covered by the requestor/organization for Jahid Hasan and team (usually 2-3 more). Do you agree?</Form.Label>
                            <div>
                                {["Yes I Agree"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="hotel_flights_agreement" // Updated name
                                        label={option}
                                        id={`hotel-${index}`}
                                        value={option}
                                        isInvalid={hasError('hotel_flights_agreement')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('hotel_flights_agreement') && errors.hotel_flights_agreement[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="credentials_agreement">
                            <Form.Label>In addition to Jahid Hasan, do you agree to provide necessary event credentials to the Passionateworlduniversity.com team members (2-3)?*</Form.Label>
                            <div>
                                {["Yes I Agree"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="credentials_agreement" // Updated name
                                        label={option}
                                        id={`team-${index}`}
                                        value={option}
                                        isInvalid={hasError('credentials_agreement')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('credentials_agreement') && errors.credentials_agreement[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="powerpoint_access">
                            <Form.Label>Will there be access to utilize a PowerPoint presentation on stage?*</Form.Label>
                            <div>
                                {["Yes","No"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="powerpoint_access" // Updated name
                                        label={option}
                                        id={`powerpoint-${index}`}
                                        value={option}
                                        isInvalid={hasError('powerpoint_access')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('powerpoint_access') && errors.powerpoint_access[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="raw_footage_agreement">
                            <Form.Label>Do you agree to send the raw footage over of Jahid Hasan's speech after the event?*</Form.Label>
                            <div>
                                {["Yes I Agree"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="raw_footage_agreement" // Updated name
                                        label={option}
                                        id={`footage-${index}`}
                                        value={option}
                                        isInvalid={hasError('raw_footage_agreement')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('raw_footage_agreement') && errors.raw_footage_agreement[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="media_presence_agreement">
                            <Form.Label>Do you agree that Passionateworlduniversity.com can have media presence during the event and permission to use footage from the event on Jahid Hasan's social media accounts?*</Form.Label>
                            <div>
                                {["Yes I Agree"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="media_presence_agreement" // Updated name
                                        label={option}
                                        id={`media-${index}`}
                                        value={option}
                                        isInvalid={hasError('media_presence_agreement')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('media_presence_agreement') && errors.media_presence_agreement[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="content_approval_agreement">
                            <Form.Label>We require that we must have final approval of all content (pre & post event materials) that has Jahid Hasan's name, image, and likeness associated with it. Do you agree?*</Form.Label>
                            <div>
                                {["Yes I Agree"].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="content_approval_agreement" // Updated name
                                        label={option}
                                        id={`approval-${index}`}
                                        value={option}
                                        isInvalid={hasError('content_approval_agreement')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('content_approval_agreement') && errors.content_approval_agreement[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="other_engagements">
                            <Form.Label>Outside of Jahid Hasan's Speaking engagement, please list any other engagements that are required at the event (Q&A, Meet and greet, photo opp, signing, etc.).*</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                name="other_engagements" // Added name attribute
                                required 
                                style={{ ...inputFieldStyle, height: '150px' }} // Applied input field style and height
                                isInvalid={hasError('other_engagements')} // Added error invalidation
                            />
                            <Form.Control.Feedback type="invalid">{hasError('other_engagements') && errors.other_engagements[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="fee_understanding">
                            <Form.Label>I understand that in exchange for Speaking services, All payments are in BDT. passionateworlduniversity.com may cancel at any time until the final payment has been made. Should the speakers become ill or unable to attend for other reasons, all fees will be returned.**</Form.Label>
                            <div>
                                {["Yes, I understand there's a speaking fee and that hotel and flights for Jahid Hasan and team won’t be covered. I’ll respect their decision either way.", "No, I did not know this and I understand Jahid Hasan may still not be able to speak at my event."].map((option, index) => (
                                    <Form.Check
                                        key={index}
                                        type="radio"
                                        name="fee_understanding" // Updated name
                                        label={option}
                                        id={`terms-${index}`}
                                        value={option}
                                        isInvalid={hasError('fee_understanding')} // Added error invalidation
                                    />
                                ))}
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('fee_understanding') && errors.fee_understanding[0]}</Form.Control.Feedback>
                        </Form.Group>

                        <div className="text-center mt-5 mb-5">
                            <Button className="btn-default" variant="primary" type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />{' '}
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Application'
                                )}
                            </Button>
                        </div>
                        {submitStatus && (
                            <Alert variant={submitStatusType} className="mt-4">
                                {submitStatus}
                            </Alert>
                        )}
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default SpeakingForm;
